import { Inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { LoginService } from '../services/login.service';
import { PGHOktaService } from '../services/pghokta.service';
import { LoggedInUserSettings } from '../services/settings/logged-in-user-settings';
import { PreInitSettingsService } from '../services/settings/pre-init-setting.service';
import { UtilityService } from '../services/utility.service';
import { StorageService } from '../services/storage.service';
import { ADPResourceService } from 'digital-core';
import { AppConstants } from '../constants';
import { PGHIdentityService } from '../services/pghidentity.service';
import { PGHIdentityAuthStateService } from '../services/pghidentity-auth-state.service';
import { PGHAppLoadService } from '../services/pgh-app-load.service';
import { ResourcesLoaderService } from 'digital-core';
import { firstValueFrom, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const OKTA_SESSION_ACTIVE = 'okta-session-active';
@Injectable({ providedIn: 'root' })
export class AuthGuard  {
  mobileErrMsg: string = 'SSO login failed';
  isNative: boolean = false;
  constructor(private oktaService: OktaAuthStateService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private router: Router,
    private loggedInUserSettings: LoggedInUserSettings,
    private preInitSettingsService: PreInitSettingsService,
    private loginService: LoginService,
    private utilityService: UtilityService,
    private pghOktaService: PGHOktaService,
    private pghIdentityService: PGHIdentityService,
    private identityAuthStateService: PGHIdentityAuthStateService,
    private storageService: StorageService,
    private resourceService: ADPResourceService,
    private appLoadService: PGHAppLoadService,
    private resourcesLoader: ResourcesLoaderService
  ) { }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // Check App Session
    let isSessionExists = await this.pghOktaService.isTokenValid() || this.pghIdentityService.isTokenValid();
    if (isSessionExists && this.pghIdentityService.isTokenValid()) {
      this.identityAuthStateService.setAuthenticated(true);
    }
    if (!isSessionExists)
      isSessionExists = await this.checkSession();
    return isSessionExists;
  }

  public async checkSession() {

    const oktaSessionExists = await this.oktaAuth.session.exists();
    let oktTokenStorage = localStorage.getItem('okta-token-storage');
    const methodName = 'checkSession';

    this.isNative = this.utilityService.isNativeApp;
    if (oktaSessionExists || this.isValidateToken(oktTokenStorage)) {
      localStorage.setItem(OKTA_SESSION_ACTIVE, 'true');
      const tokenDetail = oktaSessionExists ? await this.getToken()
        : ((oktTokenStorage) ? { isValid: this.isValidateToken(oktTokenStorage), token: JSON.parse(oktTokenStorage) }
          : { isValid: false, token: null });

      if (tokenDetail.isValid) {
        const tokens = tokenDetail.token;
        const idToken = oktaSessionExists ? tokens.tokens.idToken
          : (tokens.id_token ? tokens.id_token : tokens.idToken);

        const accessToken = oktaSessionExists ? tokens.tokens.accessToken
          : (tokens.access_token ? tokens.access_token : tokens.accessToken);

        const token = oktaSessionExists ? tokens.tokens.accessToken.accessToken
          : (tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
            : tokens.accessToken.accessToken);

        this.oktaAuth.tokenManager.add('id_token', idToken);
        this.oktaAuth.tokenManager.add('access_token', accessToken);

        const authenticated = this.pghOktaService.isTokenValid() || this.pghIdentityService.isTokenValid();

        if (!authenticated && this.isNative) {
          // if Token in Storage is Expired for Native App; Remove it and goto SSO page to get new tokens
          window.localStorage.clear();
          window.sessionStorage.setItem('referrerPath', window.location.pathname);
          this.router.navigate(['/sso']);
          return Promise.resolve(true);
        }

        const decodedToken = this.oktaAuth.token.decode(token);
        let isExists = false;
        // Check exist in group or not
        // Check if decodedToken and its payload exist and if groups is an array
        if (decodedToken && decodedToken.payload && Array.isArray(decodedToken.payload.groups)) {
          // Check if groups array has length and perform further operations
          if (decodedToken.payload.groups.length > 0) {
            isExists = decodedToken.payload.groups.some(x =>
              x.toString().toLowerCase().trim() === this.resourceService.getPortalSetting('Global_SiteSettings_GroupName')?.Value?.toLowerCase()?.trim()
            );
          }
        }

        if (authenticated && !isExists) {
          // Send Error log in case of native app
          this.sendErrorLog(`Method: ${methodName}; Error: User authenticated but user do not exist in the group okta`);
          await this.throwAccountNotFoundError(true);
          return Promise.resolve(false);
        } else if (!authenticated && !isExists) {
          this.loginService.failedLoginBycontract = false;
          // Send Error log in case of native app
          await this.clearSession(`User not authenticated and do no exist in the group okta`);
          return Promise.resolve(false);
        } else if(!this.loginService.selectedPlanNumber) {
          const plansCount = Number(this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.PlansCount) || '0');
          //console.log('No selected plan number found in LoginService during AuthGuard checkSession.', plansCount);
          // Redirect to select plan page if there are multiple plans
          if(plansCount > 1 && this.resourceService.getPortalSetting('Global_SelectHome_Enabled')?.Value?.toLowerCase() !== 'false') {
            const selectPlanUrl = this.resourceService.getPortalSetting('URL_User_Select_Plan').Value;
            this.router.navigate([selectPlanUrl]);
            return Promise.resolve(true);
          }
        }
        this.loginService.failedLoginByUserGroup = false;
        return Promise.resolve(true);
      } else {
        // Send Error log in case of native app
        await this.clearSession(`Token is invalid`);
        return Promise.resolve(false);
      }
    } else {
      // Redirect to login flow.
      const authenticated = await this.oktaAuth.isAuthenticated();
      if (authenticated && !this.loggedInUserSettings.isUserInCurrentGroup()) {
        if (localStorage.getItem(OKTA_SESSION_ACTIVE) === 'true') {
          localStorage.setItem(OKTA_SESSION_ACTIVE, null);
          await this.clearSession(`OKTA_SESSION_ACTIVE = true & User authenticated but user do not exist in the group`);
        } else {
          // Send Error log in case of native app
          this.sendErrorLog(`Method: ${methodName}; Error: OKTA_SESSION_ACTIVE = ${localStorage.getItem(OKTA_SESSION_ACTIVE)} & User authenticated but user do not exist in the group`);
          localStorage.setItem(OKTA_SESSION_ACTIVE, null);
          await this.throwAccountNotFoundError(true);
        }
        // Send Error log in case of native app
        this.sendErrorLog(`Method: ${methodName}; Error: User authenticated but user do not exist in the group okta`);
        return Promise.resolve(false);
      }
      else if (authenticated && this.loggedInUserSettings.isUserInCurrentGroup()) {
        return Promise.resolve(true);
      }
      else if (this.isNative) {
        window.sessionStorage.setItem('referrerPath', window.location.pathname);

        this.router.navigate(['/sso']);
        return Promise.resolve(true);

      } else if (!authenticated) {
        const identityTokenResult = this.checkIdentityToken();
        if (identityTokenResult) {
          this.identityAuthStateService.setAuthenticated(true);
          return Promise.resolve(true);
        }
      }
      // Send Error log in case of native app
      await this.clearSession(`User not authenticated and user do not exist in the group okta`);
      return Promise.resolve(false);
    }
  }

  private async throwAccountNotFoundError(failedLoginByUserGroup: boolean) {
    this.loginService.failedLoginByUserGroup = failedLoginByUserGroup;
    await this.clearSession(`Account not found`);
  }

  private async clearSession(msg: string) {

    msg = msg ? msg : this.mobileErrMsg;
    // Send Error log in case of native app
    this.sendErrorLog(`Method: ClearSession; Error: ${msg}`);

    var redirectToTechSupport = this.loginService.redirectToTechSupport;
    this.oktaAuth.tokenManager.clear();
    await this.oktaAuth.closeSession();
    await this.oktaAuth.revokeAccessToken();
    this.storageService.clearLocal();

    var failedLoginBycontract = this.loginService.failedLoginBycontract;
    sessionStorage.clear();
    if (redirectToTechSupport) {
      failedLoginBycontract ? this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_NoContract'}}) : this.router.navigate(['/tech-support']);
    } else {
      await this.ensureSiteResourcesLoaded();
      if (this.resourceService.getPortalSetting('Home_Page_Enabled')?.Value === 'true') {
        this.router.navigate(['/home']);
      } else if (this.resourceService.getPortalSetting('Global_Login_Disabled')?.Value?.toLowerCase()?.trim() === 'true') {
        this.router.navigate(['/lookup-account']);
      } else {
        this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
      }
    }
  }

  /**
   * clearSession can run before PageResourcesResolver has populated portal settings (e.g. a
   * cold load that fails auth immediately), so Global_Login_Disabled would read null. Mirrors
   * ServiceRequestActivateGuard's ensureSiteResourcesLoaded: drive the load ourselves and
   * fail-open so a load error never blocks the redirect.
   */
  private async ensureSiteResourcesLoaded(): Promise<void> {
    if (this.appLoadService.isSiteResourcesLoaded.getValue()) {
      return;
    }
    try {
      await firstValueFrom(from(window['__bootstrapConfigPromise'] ?? Promise.resolve()).pipe(switchMap(() => this.appLoadService.loadSiteResources())));
      const pageResources = await firstValueFrom(this.resourcesLoader.loadAllPageResources('Global', false, true));
      this.resourceService.setGlobalResources(pageResources.resources);
      this.resourceService.setGlobalLabels(pageResources.labels);
      this.resourceService.setPortalPageList(pageResources.lists);
      this.appLoadService.isSiteResourcesLoaded.next(true);
    } catch {
      // Fail-open: if resources can't be loaded, fall through to the default /login redirect.
    }
  }

  async getToken() {
    return this.oktaAuth.token.getWithoutPrompt({
      scopes: this.preInitSettingsService.getSettings().okta.scopes,
      issuer: this.preInitSettingsService.getSettings().okta.issuer
    }).then((res) => {
      return Promise.resolve({ isValid: true, token: res });
    }).catch(err => {
      return Promise.resolve({ isValid: false, token: null });
    });
  }

  isValidateToken(oktTokenStorage: string) {
    const tokens = JSON.parse(oktTokenStorage);
    try {
    const idToken = tokens.id_token ? tokens.id_token : tokens.idToken;
    const accessToken = tokens.access_token ? tokens.access_token : tokens.accessToken;
    const token = tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
      : (tokens.accessToken ? tokens.accessToken.accessToken : null);
    let isValid = false;
    if (idToken && accessToken && token) {
      isValid = true;
    }
    return isValid;
    } catch  {
      return false;
    }
  }

  private checkIdentityToken(): boolean {
    const result = this.pghIdentityService.processIdentityToken();
    if (!result.isValid) {
      return false;
    }

    // Set the first plan number if available
    if (!this.loginService.selectedPlanNumber) {
      if (result.planNumbers.length > 1 && this.resourceService.getPortalSetting('Global_SelectHome_Enabled')?.Value?.toLowerCase() !== 'false') {
        const selectPlanUrl = this.resourceService.getPortalSetting('URL_User_Select_Plan').Value;
        this.router.navigate([selectPlanUrl]);
      } else {
        this.loginService.selectedPlanNumber = result.planNumbers[0];
      }
    }

    this.loginService.failedLoginByUserGroup = false;
    return true;
  }

  sendErrorLog(msg: string) {
    console.log(`log : ${msg}`);
    const rnWindow = window as any;
    if (rnWindow.ReactNativeWebView) {
      const message = JSON.stringify({ action: 'loginError', args: { message: msg } });
      rnWindow.ReactNativeWebView.postMessage(message);
    }
  }
}
